<script lang="ts">
  // ============================================================
  // ✅ Svelte 5 Runes Component
  // ============================================================

  import { notifications, type Notification, type NotificationType } from '../../stores/notifications';
  import { onMount } from 'svelte';
  import { router } from '@inertiajs/svelte';
  import Skeleton from '@/components/ui/Skeleton.svelte';

  let selectedType = $state<NotificationType | 'all'>('all');

  onMount(() => {
    notifications.fetch(true);
  });

  function filterByType(type: NotificationType | 'all') {
    selectedType = type;
  }

  function getNotificationIcon(type: string): { path: string; color: string; bg: string } {
    const icons: Record<string, { path: string; color: string; bg: string }> = {
      post_like: { path: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z', color: 'text-error', bg: 'bg-error-light dark:bg-error/15' },
      post_comment: { path: 'M7 8h10M7 12h4m1 8l-4-2H5a2 2 0 01-2-2V8a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2l-4 2z', color: 'text-primary', bg: 'bg-primary-50 dark:bg-primary/15' },
      comment_reply: { path: 'M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6', color: 'text-info', bg: 'bg-info-light dark:bg-info/15' },
      mention: { path: 'M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9', color: 'text-primary', bg: 'bg-primary-50 dark:bg-primary/15' },
      follow: { path: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z', color: 'text-success', bg: 'bg-success-light dark:bg-success/15' },
      dm_received: { path: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', color: 'text-info', bg: 'bg-info-light dark:bg-info/15' },
      system_notice: { path: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', color: 'text-gray-500', bg: 'bg-surface-tertiary' },
      report_resolved: { path: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', color: 'text-success', bg: 'bg-success-light dark:bg-success/15' },
      report_received: { path: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z', color: 'text-warning', bg: 'bg-warning-light dark:bg-warning/15' },
      moderation_action: { path: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z', color: 'text-warning', bg: 'bg-warning-light dark:bg-warning/15' },
    };
    return icons[type] || { path: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9', color: 'text-gray-500', bg: 'bg-surface-tertiary' };
  }

  function getNotificationMessage(notification: Notification): string {
    const actor = notification.actor?.name || '누군가';
    const messages: Record<string, string> = {
      post_like: `${actor}님이 게시글을 좋아합니다`,
      post_comment: `${actor}님이 댓글을 남겼습니다`,
      comment_reply: `${actor}님이 답글을 남겼습니다`,
      mention: `${actor}님이 회원님을 언급했습니다`,
      follow: `${actor}님이 팔로우했습니다`,
      new_follower: `${actor}님이 회원님을 팔로우하기 시작했습니다`,
      dm_received: `${actor}님의 새 메시지`,
      system_notice: (notification.metadata?.message as string) || '시스템 알림',
      report_resolved: '신고가 처리되었습니다',
      report_received: '새 신고가 접수되었습니다',
      moderation_action: '관리자 조치가 있습니다',
      badge_earned: '새 배지를 받았습니다',
      level_up: '레벨이 올랐습니다',
      reputation_milestone: '평판 목표를 달성했습니다',
    };
    return messages[notification.type] || '새 알림';
  }

  /// 알림이 가리키는 곳으로 보낸다.
  ///
  /// 🔴 `notifiable` 을 서버가 내려주는데 화면이 쓰지 않으면, 알림은 "무슨 일이
  /// 있었다" 만 말하고 그 일로 데려다주지는 않는 반쪽이 된다. 모르는 대상
  /// 타입은 **이동하지 않는다** — 추측해서 만든 경로로 보내면 404 가 뜨고,
  /// 사용자는 알림이 깨진 것으로 읽는다.
  function targetPath(notification: Notification): string | null {
    const id = notification.notifiableId;
    if (!id) return null;
    switch (notification.notifiableType) {
      case 'Post': return `/posts/${id}`;
      case 'Comment': {
        const postId = notification.metadata?.postId as string | undefined;
        return postId ? `/posts/${postId}#comment-${id}` : null;
      }
      default: return null;
    }
  }

  function openNotification(notification: Notification) {
    notifications.markAsRead(notification.id);
    const path = targetPath(notification);
    if (path) router.visit(path);
  }

  function formatTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return '방금 전';
    if (diffMins < 60) return `${diffMins}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    if (diffDays < 7) return `${diffDays}일 전`;
    return date.toLocaleDateString('ko-KR');
  }

  const filteredNotifications = $derived.by(() => {
    const list = $notifications.notifications || [];
    return selectedType === 'all' ? list : list.filter((n) => n.type === selectedType);
  });

  const typeFilters: { value: NotificationType | 'all'; label: string }[] = [
    { value: 'all', label: '전체' },
    { value: 'post_like', label: '좋아요' },
    { value: 'post_comment', label: '댓글' },
    { value: 'mention', label: '멘션' },
    { value: 'dm_received', label: '메시지' },
    { value: 'system_notice', label: '시스템' },
  ];
</script>

<svelte:head>
  <title>알림 | AX Admin</title>
</svelte:head>

<div class="space-y-6">
  <div class="max-w-3xl mx-auto">
    <div class="flex items-start justify-between gap-4 mb-6">
      <div>
        <h1 class="text-h1 text-content">알림</h1>
        <p class="mt-2 text-body2 text-content-tertiary">내 활동과 관련된 업데이트를 확인하세요.</p>
      </div>
      {#if $notifications.unreadCount > 0}
        <button
          onclick={() => notifications.markAllAsRead()}
          class="btn btn-ghost btn-sm"
        >
          모두 읽음
        </button>
      {/if}
    </div>

    <!-- Type Filters -->
    <div class="flex flex-wrap gap-2 mb-6">
      {#each typeFilters as filter}
        <button
          onclick={() => filterByType(filter.value)}
          class="px-4 py-2 rounded-xl text-body2 transition-all
                 {selectedType === filter.value 
                   ? 'bg-primary text-white' 
                   : 'bg-surface-tertiary text-content-secondary hover:bg-gray-200 dark:hover:bg-dark-elevated'}"
        >
          {filter.label}
        </button>
      {/each}
    </div>

    <!-- Notification List -->
    <div class="card overflow-hidden">
      {#if $notifications.isLoading && ($notifications.notifications?.length || 0) === 0}
        <div class="p-6 space-y-4">
          {#each Array(5) as _}
            <div class="flex items-start gap-4">
              <Skeleton width="40px" height="40px" rounded="full" />
              <div class="flex-1">
                <Skeleton width="75%" height="16px" rounded="md" />
                <div class="mt-2">
                  <Skeleton width="25%" height="12px" rounded="md" />
                </div>
              </div>
            </div>
          {/each}
        </div>
      {:else if filteredNotifications.length === 0}
        <div class="p-12 text-center">
          <svg class="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-dark-text-disabled" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <p class="text-body1 text-content-tertiary">알림이 없습니다</p>
        </div>
      {:else}
        {#each filteredNotifications as notification (notification.id)}
          {@const icon = getNotificationIcon(notification.type)}
          <div
            class="flex items-start gap-4 p-4 border-b border-default last:border-b-0
                   hover:bg-gray-50 dark:hover:bg-dark-tertiary transition-colors cursor-pointer
                   {notification.status === 'unread' ? 'bg-primary-50/50 dark:bg-primary/5' : ''}"
            onclick={() => openNotification(notification)}
            role="button"
            tabindex="0"
            onkeydown={(e) => e.key === 'Enter' && openNotification(notification)}
          >
            <!-- Icon -->
            <div class="w-10 h-10 rounded-full {icon.bg} flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 {icon.color}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d={icon.path}/>
              </svg>
            </div>

          <!-- Content -->
          <div class="flex-1 min-w-0">
            <div class="flex items-start justify-between gap-2">
              <div class="flex-1 min-w-0">
                <p class="text-body2 text-content {notification.status === 'unread' ? 'font-medium' : ''}">
                  {getNotificationMessage(notification)}
                </p>
              </div>

              <div class="flex items-center gap-2 flex-shrink-0">
                <span class="text-caption text-gray-400 dark:text-dark-text-disabled">
                  {formatTime(notification.createdAt)}
                </span>
                {#if notification.status === 'unread'}
                  <span class="w-2 h-2 bg-primary rounded-full"></span>
                {/if}
              </div>
            </div>
          </div>
          </div>
        {/each}

        {#if $notifications.hasMore}
          <button
            onclick={() => notifications.fetch()}
            disabled={$notifications.isLoading}
            class="w-full py-4 text-center text-primary hover:bg-gray-50 dark:hover:bg-dark-tertiary transition-colors
                   disabled:opacity-50 disabled:cursor-not-allowed text-body2"
          >
            {$notifications.isLoading ? '로딩 중...' : '더 보기'}
          </button>
        {/if}
      {/if}
    </div>
  </div>
</div>
